// src/components/OfferModal.js

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { createOffer } from "../utils/offerEngine";
import { X, AlertCircle } from "lucide-react";
import OFFER_CONFIG from "../config/offerConfig";

export default function OfferModal({ product, onClose, onSend, recipientId, chatId, counterToOfferId = null, initialAmount = "" }) {
  const { t } = useTranslation(["inbox", "common"]);
  const { user } = useAuth();
  const [amount, setAmount] = useState(initialAmount != null ? String(initialAmount) : "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Format amount for display
  const formattedAmount = useMemo(() => {
    if (!amount || Number(amount) <= 0) return "";
    const num = Number(amount);
    return Number.isFinite(num) ? num.toLocaleString("en-US") : "";
  }, [amount]);

  const isValid = amount && Number(amount) > 0;
  const sellerPrice = Number(product?.price);
  const minOffer = Number.isFinite(sellerPrice) && sellerPrice > 0 ? Math.ceil(sellerPrice * 0.4) : null;

  useEffect(() => {
    setAmount(initialAmount != null ? String(initialAmount) : "");
  }, [initialAmount]);

  const validationMessage = useMemo(() => {
    if (!Number.isFinite(sellerPrice) || sellerPrice <= 0) return "";
    return t("inbox:offerRangeHint", {
      defaultValue: "Offer range: {{min}} RWF to {{max}} RWF",
      min: minOffer?.toLocaleString?.() || minOffer,
      max: sellerPrice.toLocaleString(),
    });
  }, [t, minOffer, sellerPrice]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");

    // Validation
    if (!isValid) {
      setError(t("inbox:offerAmountInvalid", "Please enter a valid amount."));
      return;
    }

    const offerValue = Number(amount);
    if (Number.isFinite(sellerPrice) && sellerPrice > 0) {
      if (offerValue > sellerPrice) {
        setError(
          t("inbox:offerAmountTooHigh", {
            defaultValue: "Offer cannot exceed seller price ({{max}} RWF).",
            max: sellerPrice.toLocaleString(),
          })
        );
        return;
      }
      if (offerValue < minOffer) {
        setError(
          t("inbox:offerAmountTooLow", {
            defaultValue: "Offer must be at least 40% of seller price ({{min}} RWF).",
            min: minOffer.toLocaleString(),
          })
        );
        return;
      }
    }

    setSending(true);

    try {
      // In chat contexts, recipientId is the negotiation counterpart.
      const targetUserId = recipientId || product?.poster?.userId || product?.userId || product?.sellerId;
      if (!targetUserId) {
        throw new Error(t("inbox:sellerUnavailable", "Seller information unavailable."));
      }

      // Prevent self-offers only when counterpart resolves to self.
      if (user.uid === targetUserId) {
        throw new Error(t("inbox:ownProductOfferError", "You cannot make an offer on your own product."));
      }

      // Create offer with integrated chat
      const res = await createOffer({
        chatId: chatId || null,
        productId: product.id,
        fromUserId: user.uid,
        toUserId: targetUserId,
        amount: Number(amount),
        message: "",
        counterToOfferId: counterToOfferId || null,
      });

      if (!res?.chatId && !res?.offer?.chatId) {
        throw new Error(t("inbox:offerConversationFailed", "Failed to create conversation."));
      }

      const targetChatId = res.chatId || res.offer?.chatId;

      // Close modal first for smooth UX
      onClose?.();

      // Trigger callback if provided
      if (onSend) {
        onSend({ offer: res.offer || res, chatId: targetChatId });
      }

      // Navigate to messages for continued conversation
      setTimeout(() => {
        navigate(`/messages/${encodeURIComponent(targetChatId)}`);
      }, 300);
    } catch (err) {
      // User-friendly error messages
      const errorMsg =
        err.message || t("inbox:offerSendFailed", "Unable to send offer. Please check your connection and try again.");
      setError(errorMsg);
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div
      className={OFFER_CONFIG.MODAL.backdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={OFFER_CONFIG.MODAL.container}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={OFFER_CONFIG.MODAL.header}>{t("inbox:makeOffer", "Make an offer")}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition text-gray-700 dark:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Price Input */}
          <div>
            <label className={`${OFFER_CONFIG.MODAL.label} block mb-2`}>
              {t("inbox:suggestPrice", "Suggest a price")}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 font-medium text-sm">
                RWF
              </span>
              <input
                type="number"
                placeholder=""
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError("");
                }}
                min="1"
                step="1"
                className={`${OFFER_CONFIG.MODAL.input} pl-12`}
                required
                autoFocus
                disabled={sending}
              />
            </div>
            {formattedAmount && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                {formattedAmount} RWF
              </p>
            )}
            {validationMessage && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{validationMessage}</p>
            )}
          </div>

          {/* Info Banner */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {t("inbox:offerInfoBanner", "After sending, you can chat directly with the seller to negotiate and coordinate payment and delivery manually.")}
            </p>
          </div>

          {/* CTA Button - Updated with goldish styling */}
          <button
            type="submit"
            disabled={sending || !isValid}
            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <div className="orrith-loader orrith-loader-sm orrith-loader-on-dark" />
                {t("common:loading", "Loading...")}
              </>
            ) : (
              <>
                {t("inbox:sendOffer", "Send offer")}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}